import { DataMatchPair, DataMatchPersonSnapshot, DataMatchRunResult } from "../../../../models/DataMatchResult";

export function centralRowKey(p: DataMatchPersonSnapshot): string {
    if (p.crbcId != null && String(p.crbcId).length > 0) {
        return `crbc:${p.crbcId}`;
    }
    return `upn:${(p.upn ?? "").toLowerCase()}`;
}

export function providerRowKey(p: DataMatchPersonSnapshot): string {
    return `prov:${p.clientId ?? ""}|${p.misId ?? ""}`;
}

export function pairRowKey(pair: DataMatchPair): string {
    return `${centralRowKey(pair.central)}::${providerRowKey(pair.provider)}`;
}

export function clonePair(pair: DataMatchPair): DataMatchPair {
    return {
        central: { ...pair.central },
        provider: { ...pair.provider },
    };
}

export function cloneRunResult(r: DataMatchRunResult): DataMatchRunResult {
    return JSON.parse(JSON.stringify(r)) as DataMatchRunResult;
}

/**
 * Manual links are kept in manualPairsPreserveRef so the next "Perform data match" can merge them with fresh API results.
 * After undo or any restore, remove ref entries that no longer appear in matched pairs so undone links are not re-applied.
 */
export function syncManualPairsPreserveRefToRunResult(ref: { current: DataMatchPair[] }, run: DataMatchRunResult | null): void {
    if (!run?.matchedPairs || run.matchedPairs.length === 0) {
        ref.current = [];
        return;
    }
    const allowed = new Set(run.matchedPairs.map(pairRowKey));
    ref.current = ref.current.filter((p) => allowed.has(pairRowKey(p)));
}

/** Merge manually linked pairs into a run result (for "Link record" UI). */
export function mergeManualLinksIntoResult(prev: DataMatchRunResult, newPairs: DataMatchPair[]): DataMatchRunResult {
    if (newPairs.length === 0) {
        return prev;
    }
    const centralDrop = new Set(newPairs.map((p) => centralRowKey(p.central)));
    const providerDrop = new Set(newPairs.map((p) => providerRowKey(p.provider)));
    const unmatchedCentral = prev.unmatchedCentral.filter((c) => !centralDrop.has(centralRowKey(c)));
    const unmatchedProvider = prev.unmatchedProvider.filter((p) => !providerDrop.has(providerRowKey(p)));
    const matchedPairs = [...prev.matchedPairs, ...newPairs.map(clonePair)];
    return {
        ...prev,
        matchedCount: matchedPairs.length,
        matchedPairs,
        unmatchedCentral,
        unmatchedProvider,
    };
}

/** Merge a fresh automatic run with manually linked pairs (manual wins central/provider conflicts vs auto). */
export function mergeDataMatchRunWithManualLinks(api: DataMatchRunResult, manualPairs: DataMatchPair[]): DataMatchRunResult {
    if (manualPairs.length === 0) {
        return api;
    }
    const manualByPairKey = new Map<string, DataMatchPair>();
    for (const mp of manualPairs) {
        manualByPairKey.set(pairRowKey(mp), clonePair(mp));
    }
    const manualList = Array.from(manualByPairKey.values());
    const manualCentralKeys = new Set(manualList.map((p) => centralRowKey(p.central)));
    const manualProviderKeys = new Set(manualList.map((p) => providerRowKey(p.provider)));

    const filteredAuto = api.matchedPairs.filter((p) => {
        const ck = centralRowKey(p.central);
        const pk = providerRowKey(p.provider);
        return !manualCentralKeys.has(ck) && !manualProviderKeys.has(pk);
    });

    const mergedMatched = [...filteredAuto.map(clonePair), ...manualList.map(clonePair)];

    const centralByKey = new Map<string, DataMatchPersonSnapshot>();
    const providerByKey = new Map<string, DataMatchPersonSnapshot>();
    const addCentrals = (rows: DataMatchPersonSnapshot[]) => {
        for (const r of rows) {
            centralByKey.set(centralRowKey(r), r);
        }
    };
    const addProviders = (rows: DataMatchPersonSnapshot[]) => {
        for (const r of rows) {
            providerByKey.set(providerRowKey(r), r);
        }
    };
    for (const pair of api.matchedPairs) {
        addCentrals([pair.central]);
        addProviders([pair.provider]);
    }
    addCentrals(api.unmatchedCentral);
    addProviders(api.unmatchedProvider);
    for (const pair of manualList) {
        addCentrals([pair.central]);
        addProviders([pair.provider]);
    }

    const matchedCentralKeys = new Set(mergedMatched.map((p) => centralRowKey(p.central)));
    const matchedProviderKeys = new Set(mergedMatched.map((p) => providerRowKey(p.provider)));

    const unmatchedCentral = Array.from(centralByKey.values()).filter((c) => !matchedCentralKeys.has(centralRowKey(c)));
    const unmatchedProvider = Array.from(providerByKey.values()).filter((p) => !matchedProviderKeys.has(providerRowKey(p)));

    return {
        ...api,
        matchedCount: mergedMatched.length,
        matchedPairs: mergedMatched,
        unmatchedCentral,
        unmatchedProvider,
    };
}

/** Remove every pair in `batch` from matched and ensure both sides appear in unmatched (idempotent). */
export function undoMatchBatch(prev: DataMatchRunResult, batch: DataMatchPair[]): DataMatchRunResult {
    if (batch.length === 0) {
        return prev;
    }
    const batchKeys = new Set(batch.map(pairRowKey));
    const matchedPairs = prev.matchedPairs.filter((p) => !batchKeys.has(pairRowKey(p)));
    const centralKeys = new Set(prev.unmatchedCentral.map(centralRowKey));
    const providerKeys = new Set(prev.unmatchedProvider.map(providerRowKey));
    const unmatchedCentral = [...prev.unmatchedCentral];
    const unmatchedProvider = [...prev.unmatchedProvider];
    for (const pair of batch) {
        const ck = centralRowKey(pair.central);
        if (!centralKeys.has(ck)) {
            centralKeys.add(ck);
            unmatchedCentral.push({ ...pair.central });
        }
        const pk = providerRowKey(pair.provider);
        if (!providerKeys.has(pk)) {
            providerKeys.add(pk);
            unmatchedProvider.push({ ...pair.provider });
        }
    }
    return {
        ...prev,
        matchedCount: matchedPairs.length,
        matchedPairs,
        unmatchedCentral,
        unmatchedProvider,
    };
}
